import { computed } from 'vue'
import { useStore } from 'vuex'
import CardApi from '@/api/cardApi'
import workflowApi from '@/api/workflowApi'
import { SchemaType } from '@/constants/schema'
import safeStructuredClone from '@/utils/clone'
import { cardHelpers } from '@/mixins/cardHelpers'
import { formatCardKey } from '@/ai/utils/helpers'
import { DomainModelRole } from '@/constants/cardTypes'
import { diagramHelpers } from '@/mixins/diagramHelpers'
import { sanitizeCardDescription } from '@/shared/utils/sanitize'
import { validateWorkflow as validateWorkflowUtil } from '@/utils/domainModelValidator'
import {
  parseEntityResponse,
  parseCommandFields,
  parseReadModelFields,
  resolveSchema,
  resolveDomainEvent,
  resolveLane
} from '@/shared/utils/workflowSpec'
import {
  generateCardsFromDescription,
  generateEntitiesFromDescription,
  generateEntitiesFromWorkflow,
  generateReadModelsFromDescription,
  generateWorkflowCards,
  generateWorkflowDiagram,
  generateWorkflowReadModels,
  generateWorkflowCommands,
  generateCommandsFromDescription
} from '@/ai'

export const functionType = {
  UPDATE_WORKFLOW_NAME: 'updateWorkflowName',
  CREATE_EVENT: 'createEvent',
  UPDATE_EVENT_DESCRIPTION: 'updateEventDescription',
  DELETE_EVENT: 'deleteEvent',
  CREATE_LANE: 'createLane',
  UPDATE_LANE_NAME: 'updateLaneName',
  DELETE_LANE: 'deleteLane',
  MOVE_LANE: 'moveLane',
  CREATE_CARD: 'createCard',
  DELETE_CARD: 'deleteCard',
  UPDATE_CARD: 'updateCard',
  VOTE_CARD: 'voteCard',
  CREATE_ENTITY: 'createEntity',
  UPDATE_ENTITY: 'updateEntity',
  DELETE_ENTITY: 'deleteEntity',
  CREATE_COMMAND: 'createCommand',
  UPDATE_COMMAND: 'updateCommand',
  CREATE_DOMAIN_EVENT_SCHEMA: 'createDomainEventSchema',
  UPDATE_DOMAIN_EVENT_SCHEMA: 'updateDomainEventSchema',
  CREATE_READ_MODEL: 'createReadModel',
  UPDATE_READ_MODEL: 'updateReadModel',
  REGENERATE_CARDS: 'regenerateCards',
  ADD_MISSING_CARDS: 'addMissingCards',
  REGENERATE_COMMANDS: 'regenerateCommands',
  ADD_MISSING_COMMANDS: 'addMissingCommands',
  REGENERATE_READ_MODELS: 'regenerateReadModels',
  ADD_MISSING_READ_MODELS: 'addMissingReadModels',
  REGENERATE_ENTITIES: 'regenerateEntities',
  ADD_MISSING_ENTITIES: 'addMissingEntities',
  GENERATE_ENTITIES_FROM_DESCRIPTION: 'generateEntitiesFromDescription',
  GENERATE_COMMANDS_FROM_DESCRIPTION: 'generateCommandsFromDescription',
  GENERATE_READ_MODELS_FROM_DESCRIPTION: 'generateReadModelsFromDescription',
  GENERATE_CARDS_FROM_DESCRIPTION: 'generateCardsFromDescription',
  PLAN_WORKFLOW_WITH_AI: 'planWorkflowWithAI',
  VALIDATE_DOMAIN_MODEL: 'validateDomainModel',
  ANALYZE_DOMAIN_MODEL: 'analyzeDomainModel',
  ROLLBACK_WORKFLOW: 'rollbackWorkflow'
}

export const useWorkflowAssistantFunctions = () => {
  const store = useStore()

  const workflow = computed(() => store.state.workflow.workflow)
  const svgGraph = computed(() => store.getters['svgGraphData/svgGraphData'])
  const svgLanes = computed(() => store.getters['svgGraphData/svgLanesData'])
  const svgGroups = computed(() => store.getters['svgGraphData/svgGroupsData'])
  const cardTypes = computed(() => store.state.cardTypes.cardTypes)

  const findEventByResponse = (data) => {
    const eventName = data.domainEventName || data.name
    const lane = data.role
      ? Object.values(svgLanes.value).find((l) => l.name === data.role)
      : null

    if (lane) {
      return workflow.value.domainEvents.find((e) => e.description === eventName && e.laneId === lane.id)
    }

    return workflow.value.domainEvents.find((e) => e.description === eventName)
  }

  const updateWorkflowName = async (args) => {
    const { name } = args

    return await workflowApi.updateWorkflowName({
      ...workflow.value,
      name,
      updateNote: {
        action: 'changed workflow name to',
        target: name
      }
    })
  }

  const createEvent = async (args) => {
    const { description, laneName, parentEvent: parentEventRef, parallel } = args

    const parentEvent = parentEventRef === 'start'
      ? null
      : resolveDomainEvent(parentEventRef, workflow.value)

    return await workflowApi.addEventToWorkflow({
      type: 'bpmn:Task',
      parents: parentEvent ? [parentEvent.id] : ['start'],
      description
    },
    { laneName, parallel: parallel === true },
    safeStructuredClone(svgLanes.value),
    safeStructuredClone(svgGraph.value),
    safeStructuredClone(svgGroups.value),
    workflow.value)
  }

  const updateEventDescription = async (args) => {
    const { event: eventRef, description } = args

    const resolvedEvent = resolveDomainEvent(eventRef, workflow.value)
    if (!resolvedEvent) {
      return
    }
    const eventId = resolvedEvent.id

    if (!svgGraph.value[eventId]) {
      return
    }

    const updatedSvgGraph = safeStructuredClone(svgGraph.value)
    updatedSvgGraph[eventId].name = description

    return await workflowApi.updateWorkflow({
      svgGraph: updatedSvgGraph,
      svgLanes: svgLanes.value,
      svgGroups: svgGroups.value,
      workflow: workflow.value,
      updateNote: {
        action: svgGraph.value[eventId].type === 'bpmn:Task' ? 'updated event text to' : 'updated decision text to',
        target: description
      }
    })
  }

  const deleteEvent = async (args) => {
    const { event: eventRef } = args

    const resolvedEvent = resolveDomainEvent(eventRef, workflow.value)
    if (!resolvedEvent) {
      return
    }

    return await workflowApi.deleteEventFromWorkflow(
      resolvedEvent.id,
      safeStructuredClone(svgGraph.value),
      safeStructuredClone(svgLanes.value),
      safeStructuredClone(svgGroups.value),
      workflow.value
    )
  }

  const createLane = async (args) => {
    const { name, position } = args
    const lanes = workflow.value.lanes || []

    if (typeof position === 'number' && position >= 0 && position < lanes.length) {
      const direction = position === 0 ? 'above' : 'below'
      const relativeLaneId = position === 0 ? lanes[0].id : lanes[position - 1].id

      return await diagramHelpers.methods.addLaneRelativeTo.call(
        {
          workflow: workflow.value,
          svgGraph: svgGraph.value,
          svgLanes: svgLanes.value,
          svgGroups: svgGroups.value
        },
        relativeLaneId,
        direction,
        name
      )
    }

    return await diagramHelpers.methods.addLane(
      safeStructuredClone(svgGraph.value),
      safeStructuredClone(svgLanes.value),
      safeStructuredClone(svgGroups.value),
      workflow.value,
      name
    )
  }

  const updateLaneName = async (args) => {
    const { lane: laneName, name } = args

    const resolvedLane = resolveLane(laneName, workflow.value)
    if (!resolvedLane) {
      return
    }
    const laneId = resolvedLane.id

    const updatedSvgLanes = safeStructuredClone(svgLanes.value)
    updatedSvgLanes[laneId].name = name

    return await workflowApi.updateWorkflow({
      svgLanes: updatedSvgLanes,
      svgGraph: svgGraph.value,
      svgGroups: svgGroups.value,
      workflow: workflow.value,
      updateNote: {
        action: 'updated lane name to',
        target: name
      }
    })
  }

  const deleteLane = async (args) => {
    const { lane: laneName } = args

    const resolvedLane = resolveLane(laneName, workflow.value)
    if (!resolvedLane) {
      return
    }
    const laneId = resolvedLane.id

    const updatedSvgLanes = safeStructuredClone(svgLanes.value)
    delete updatedSvgLanes[laneId]

    return await workflowApi.updateWorkflow({
      svgLanes: updatedSvgLanes,
      svgGraph: svgGraph.value,
      svgGroups: svgGroups.value,
      workflow: workflow.value,
      updateNote: {
        action: 'deleted lane',
        target: laneName
      }
    })
  }

  const moveLane = async (args) => {
    const { lane: laneName, targetIndex } = args

    const lanesArray = Object.values(svgLanes.value).sort((a, b) => a.index - b.index)
    const currentIndex = lanesArray.findIndex((lane) => lane.name === laneName)

    if (currentIndex === -1 || targetIndex < 0 || targetIndex >= lanesArray.length) {
      console.error('Invalid lane move operation')

      return
    }

    const [movedLane] = lanesArray.splice(currentIndex, 1)
    lanesArray.splice(targetIndex, 0, movedLane)

    const newSvgLanes = {}
    lanesArray.forEach((lane, index) => {
      newSvgLanes[lane.id] = {
        ...lane,
        index
      }
    })

    return await workflowApi.updateWorkflow({
      svgGraph: svgGraph.value,
      svgLanes: newSvgLanes,
      svgGroups: svgGroups.value,
      workflow: workflow.value,
      updateNote: {
        action: 'moved lane',
        target: `${movedLane.name} to position ${targetIndex + 1}`
      }
    })
  }

  const findCardByDescription = (eventRef, description) => {
    const resolvedEvent = resolveDomainEvent(eventRef, workflow.value)
    if (!resolvedEvent) {
      return { error: 'Event not found' }
    }

    const sanitized = sanitizeCardDescription(description)
    const card = (resolvedEvent.cards || []).find((c) =>
      sanitizeCardDescription(c.description || '') === sanitized
    )

    if (!card) {
      return { error: `Card "${description}" not found on event "${resolvedEvent.description}"` }
    }

    return { card, event: resolvedEvent }
  }

  const createCard = async (args) => {
    const { domainEvent: eventRef, cardTypeId, description } = args

    const resolvedEvent = resolveDomainEvent(eventRef, workflow.value)
    if (!resolvedEvent) {
      return { success: false, error: 'Event not found' }
    }

    return await workflowApi.createCard(workflow.value.id, resolvedEvent.id, {
      cardTypeId,
      description
    })
  }

  const deleteCard = async (args) => {
    const { domainEvent: eventRef, description } = args

    const result = findCardByDescription(eventRef, description)
    if (result.error) {
      return { success: false, error: result.error }
    }

    return await workflowApi.deleteCard(workflow.value.id, { cardId: result.card.id })
  }

  const updateCard = async (args) => {
    const { domainEvent: eventRef, currentDescription, description, schema: schemaRef } = args

    const result = findCardByDescription(eventRef, currentDescription)
    if (result.error) {
      return { success: false, error: result.error }
    }

    const updatePayload = { cardId: result.card.id }

    if (description !== undefined) {
      updatePayload.description = `<p>${description}</p>`
    }

    if (schemaRef !== undefined) {
      const cardTypesMap = Object.fromEntries(cardTypes.value.map((ct) => [ct.id, ct]))
      updatePayload.schemaId = schemaRef
        ? (resolveSchema(schemaRef, workflow.value, cardTypesMap)?.id || null)
        : null
    }

    return await workflowApi.updateCardsMutation(
      workflow.value.id,
      [updatePayload]
    )
  }

  const voteCard = async (args) => {
    const { domainEvent: eventRef, description, voteType } = args

    const result = findCardByDescription(eventRef, description)
    if (result.error) {
      return { success: false, error: result.error }
    }

    return await CardApi.voteCard({
      workflowId: workflow.value.id,
      input: {
        cardId: result.card.id,
        direction: voteType
      }
    })
  }

  const createEntity = async (args) => {
    const { name, fields } = args

    const newEntity = {
      name,
      type: SchemaType.ENTITY,
      fields: fields.map((item) => ({
        name: item.name,
        cardinality: item.cardinality || 'one-to-one',
        dataType: item.dataType || 'string',
        exampleData: item.exampleData || [],
        relatedEntityId: item.relatedEntity
          ? (resolveSchema(item.relatedEntity, workflow.value)?.id || null)
          : null,
        tags: item.isRequired ? ['REQUIRED'] : []
      }))
    }

    const updatedEntities = [...(workflow.value.schemas || []), newEntity]

    return await workflowApi.updateSchemas({
      schemas: updatedEntities,
      workflow: workflow.value
    })
  }

  const updateEntity = async (args) => {
    const { entity: entityRef, name, addFields, updateFields, removeFields } = args

    const resolvedEntity = resolveSchema(entityRef, workflow.value)
    if (!resolvedEntity) {
      return { success: false, error: `Entity not found: ${entityRef}` }
    }

    const schemas = workflow.value.schemas || []
    const schemaIndex = schemas.findIndex((s) => s.id === resolvedEntity.id)

    if (schemaIndex === -1) {
      return { success: false, error: 'Entity not found' }
    }

    const schema = schemas[schemaIndex]
    const currentFields = [...(schema.fields || [])]
    const changes = []

    // Remove fields
    if (removeFields && removeFields.length > 0) {
      for (const fieldName of removeFields) {
        const fieldIndex = currentFields.findIndex((f) => f.name === fieldName)
        if (fieldIndex === -1) {
          return { success: false, error: `Field "${fieldName}" not found` }
        }
        currentFields.splice(fieldIndex, 1)
        changes.push(`removed "${fieldName}"`)
      }
    }

    // Update fields
    if (updateFields && updateFields.length > 0) {
      for (const { fieldName, updates } of updateFields) {
        const fieldIndex = currentFields.findIndex((f) => f.name === fieldName)
        if (fieldIndex === -1) {
          return { success: false, error: `Field "${fieldName}" not found` }
        }

        if (updates.name && updates.name !== fieldName) {
          const existingField = currentFields.find((f) => f.name === updates.name)
          if (existingField) {
            return { success: false, error: `Field "${updates.name}" already exists` }
          }
        }

        const existingField = currentFields[fieldIndex]
        const updatedField = { ...existingField }

        if (updates.name !== undefined) {
          updatedField.name = updates.name
        }
        if (updates.cardinality !== undefined) {
          updatedField.cardinality = updates.cardinality
        }
        if (updates.isRequired !== undefined) {
          updatedField.tags = updates.isRequired ? ['REQUIRED'] : []
        }
        if (updates.dataType !== undefined) {
          updatedField.dataType = updates.dataType
        }
        if (updates.relatedEntity !== undefined) {
          updatedField.relatedEntityId = updates.relatedEntity
            ? (resolveSchema(updates.relatedEntity, workflow.value)?.id || null)
            : null
        }
        if (updates.exampleData !== undefined) {
          updatedField.exampleData = updates.exampleData
        }

        currentFields[fieldIndex] = updatedField
        changes.push(`updated "${fieldName}"`)
      }
    }

    // Add fields
    if (addFields && addFields.length > 0) {
      for (const field of addFields) {
        const existingField = currentFields.find((f) => f.name === field.name)
        if (existingField) {
          return { success: false, error: `Field "${field.name}" already exists` }
        }
        currentFields.push({
          name: field.name,
          cardinality: field.cardinality || 'one-to-one',
          tags: field.isRequired ? ['REQUIRED'] : [],
          dataType: field.dataType || 'string',
          relatedEntityId: field.relatedEntity
            ? (resolveSchema(field.relatedEntity, workflow.value)?.id || null)
            : null,
          exampleData: field.exampleData || []
        })
        changes.push(`added "${field.name}"`)
      }
    }

    // Update entity name
    const updatedSchema = {
      ...schema,
      fields: currentFields
    }

    if (name !== undefined) {
      updatedSchema.name = name
      changes.push(`renamed to "${name}"`)
    }

    if (changes.length === 0) {
      return { success: false, error: 'No changes specified' }
    }

    const updatedSchemas = safeStructuredClone(schemas)
    updatedSchemas[schemaIndex] = updatedSchema

    await workflowApi.updateSchemas({
      schemas: updatedSchemas,
      workflow: workflow.value
    })

    return { success: true, message: `Entity updated: ${changes.join(', ')}` }
  }

  const deleteEntity = async (args) => {
    const { entity: entityRef } = args

    const resolvedEntity = resolveSchema(entityRef, workflow.value)
    if (!resolvedEntity) {
      return { success: false, error: `Entity not found: ${entityRef}` }
    }

    const updatedEntities = (workflow.value.schemas || []).filter((s) => s.id !== resolvedEntity.id)

    return await workflowApi.updateSchemas({
      schemas: updatedEntities,
      workflow: workflow.value
    })
  }

  const normalizeNestedFields = (fields) => {
    if (!Array.isArray(fields) || fields.length === 0) {
      return undefined
    }

    const normalized = fields
      .filter((f) => f && typeof f.name === 'string' && f.name.trim().length > 0)
      .map((f) => {
        const nested = { name: f.name }

        if (f.description) {
          nested.description = f.description
        }
        if (f.relatedEntity) {
          nested.relatedEntityId = resolveSchema(f.relatedEntity, workflow.value)?.id || null
        }
        if (f.cardinality) {
          nested.cardinality = f.cardinality
        }
        if (f.isRequired) {
          nested.tags = ['REQUIRED']
        }
        if (f.computed) {
          nested.computed = true
        }
        if (f.isFilter) {
          nested.type = 'filter'
        }

        const child = normalizeNestedFields(f.fields)
        if (child) {
          nested.fields = child
        }
        return nested
      })

    return normalized.length > 0 ? normalized : undefined
  }

  const createCommandSchema = async (args) => {
    const { domainEvent: eventRef, name, fields } = args

    const resolvedEvent = resolveDomainEvent(eventRef, workflow.value)
    if (!resolvedEvent) {
      return { success: false, error: 'Event not found' }
    }
    const eventId = resolvedEvent.id

    const commandCardType = cardTypes.value.find((ct) => ct.domainModelRole === DomainModelRole.COMMAND)
    if (!commandCardType) {
      return { success: false, error: 'Command card type not found' }
    }

    const cardTypesMap = Object.fromEntries(cardTypes.value.map((ct) => [ct.id, ct]))
    let commandCard = (resolvedEvent.cards || []).find((card) =>
      cardTypesMap[card.cardTypeId]?.domainModelRole === DomainModelRole.COMMAND
    )

    if (commandCard?.schemaId) {
      return { success: false, error: 'Event already has a command. Use updateCommand to modify it.' }
    }

    if (!commandCard) {
      const result = await workflowApi.createCard(workflow.value.id, eventId, {
        cardTypeId: commandCardType.id,
        description: name
      })
      commandCard = result?.cards?.find((card) =>
        cardTypesMap[card.cardTypeId]?.domainModelRole === DomainModelRole.COMMAND && !card.schemaId
      ) || (result?.cards || []).slice(-1)[0]
    }

    if (!commandCard) {
      return { success: false, error: 'Failed to create command card' }
    }

    const newSchema = {
      name,
      type: SchemaType.COMMAND,
      fields: (fields || []).map((item) => {
        const schemaField = {
          name: item.name,
          cardinality: item.cardinality || 'one-to-one',
          relatedEntityId: item.relatedEntity
            ? (resolveSchema(item.relatedEntity, workflow.value)?.id || null)
            : null,
          tags: item.isRequired ? ['REQUIRED'] : []
        }

        if (item.hideInForm !== undefined) {
          schemaField.hideInForm = !!item.hideInForm
        }

        const nested = normalizeNestedFields(item.fields)
        if (nested) {
          schemaField.fields = nested
        }

        return schemaField
      })
    }

    await workflowApi.addSchemaAndLinkCard({
      schema: newSchema,
      cardId: commandCard.id,
      eventId,
      workflow: workflow.value,
      svgGraph: svgGraph.value
    })

    return { success: true }
  }

  const updateCommandFields = async (args) => {
    const { command: commandRef, name: newName, addFields, updateFields, removeFields } = args

    const cardTypesMap = Object.fromEntries(cardTypes.value.map((ct) => [ct.id, ct]))
    const resolved = resolveSchema(commandRef, workflow.value, cardTypesMap)
    if (!resolved) {
      return { success: false, error: 'Command not found' }
    }

    const schemas = workflow.value.schemas || []
    const schemaIndex = schemas.findIndex((s) => s.id === resolved.id)

    if (schemaIndex === -1) {
      return { success: false, error: 'Command schema not found' }
    }

    const schema = schemas[schemaIndex]
    const currentFields = [...(schema.fields || [])]
    const changes = []

    if (removeFields && removeFields.length > 0) {
      for (const fieldName of removeFields) {
        const fieldIndex = currentFields.findIndex((f) => f.name === fieldName)
        if (fieldIndex === -1) {
          return { success: false, error: `Field "${fieldName}" not found` }
        }
        currentFields.splice(fieldIndex, 1)
        changes.push(`removed "${fieldName}"`)
      }
    }

    if (updateFields && updateFields.length > 0) {
      for (const { fieldName, updates } of updateFields) {
        const fieldIndex = currentFields.findIndex((f) => f.name === fieldName)
        if (fieldIndex === -1) {
          return { success: false, error: `Field "${fieldName}" not found` }
        }

        if (updates.name && updates.name !== fieldName) {
          const existingField = currentFields.find((f) => f.name === updates.name)
          if (existingField) {
            return { success: false, error: `Field "${updates.name}" already exists` }
          }
        }

        const existingField = currentFields[fieldIndex]
        const updatedField = { ...existingField }

        if (updates.name !== undefined) {
          updatedField.name = updates.name
        }
        if (updates.cardinality !== undefined) {
          updatedField.cardinality = updates.cardinality
        }
        if (updates.isRequired !== undefined) {
          updatedField.tags = updates.isRequired ? ['REQUIRED'] : []
        }
        if (updates.hideInForm !== undefined) {
          updatedField.hideInForm = !!updates.hideInForm
        }
        if (updates.relatedEntity !== undefined) {
          updatedField.relatedEntityId = updates.relatedEntity
            ? (resolveSchema(updates.relatedEntity, workflow.value)?.id || null)
            : null
          if (!updatedField.relatedEntityId) {
            delete updatedField.fields
          }
        }
        if (updates.fields !== undefined) {
          const nested = normalizeNestedFields(updates.fields)
          if (nested) {
            updatedField.fields = nested
          } else {
            delete updatedField.fields
          }
        }

        currentFields[fieldIndex] = updatedField
        changes.push(`updated "${fieldName}"`)
      }
    }

    if (addFields && addFields.length > 0) {
      for (const field of addFields) {
        const existingField = currentFields.find((f) => f.name === field.name)
        if (existingField) {
          return { success: false, error: `Field "${field.name}" already exists` }
        }
        const newField = {
          name: field.name,
          cardinality: field.cardinality || 'one-to-one',
          relatedEntityId: field.relatedEntity
            ? (resolveSchema(field.relatedEntity, workflow.value)?.id || null)
            : null,
          tags: field.isRequired ? ['REQUIRED'] : []
        }

        if (field.hideInForm !== undefined) {
          newField.hideInForm = !!field.hideInForm
        }

        const nested = normalizeNestedFields(field.fields)
        if (nested) {
          newField.fields = nested
        }

        currentFields.push(newField)
        changes.push(`added "${field.name}"`)
      }
    }

    if (changes.length === 0) {
      return { success: false, error: 'No changes specified' }
    }

    const updatedSchemas = safeStructuredClone(schemas)
    updatedSchemas[schemaIndex].fields = currentFields

    if (newName) {
      updatedSchemas[schemaIndex].name = newName
    }

    await workflowApi.updateSchemas({
      schemas: updatedSchemas,
      workflow: workflow.value
    })

    return { success: true, message: `Command updated: ${changes.join(', ')}` }
  }

  const createDomainEventSchemaFn = async (args) => {
    const { domainEvent: eventRef, name, fields, entity: entityRef } = args

    if (!name || !name.trim()) {
      return { success: false, error: 'name is required' }
    }

    const resolvedEvent = resolveDomainEvent(eventRef, workflow.value)
    if (!resolvedEvent) {
      return { success: false, error: 'Event not found' }
    }
    const eventId = resolvedEvent.id

    const domainEventCardType = cardTypes.value.find((ct) => ct.domainModelRole === DomainModelRole.DOMAIN_EVENT)
    if (!domainEventCardType) {
      return { success: false, error: 'Domain Event card type not found' }
    }

    const cardTypesMap = Object.fromEntries(cardTypes.value.map((ct) => [ct.id, ct]))
    let domainEventCard = (resolvedEvent.cards || []).find((card) =>
      cardTypesMap[card.cardTypeId]?.domainModelRole === DomainModelRole.DOMAIN_EVENT
    )

    if (domainEventCard?.schemaId) {
      return { success: false, error: 'Event already has a domain event schema. Use updateDomainEventSchema to modify it.' }
    }

    if (!domainEventCard) {
      const result = await workflowApi.createCard(workflow.value.id, eventId, {
        cardTypeId: domainEventCardType.id,
        description: name
      })
      domainEventCard = result?.cards?.find((card) =>
        cardTypesMap[card.cardTypeId]?.domainModelRole === DomainModelRole.DOMAIN_EVENT && !card.schemaId
      ) || (result?.cards || []).slice(-1)[0]
    }

    if (!domainEventCard) {
      return { success: false, error: 'Failed to create domain event card' }
    }

    const entityId = entityRef ? (resolveSchema(entityRef, workflow.value)?.id || null) : null

    const newSchema = {
      name,
      type: SchemaType.DOMAIN_EVENT,
      entityId,
      fields: (fields || []).map((item) => {
        const schemaField = {
          name: item.name,
          cardinality: item.cardinality || 'one-to-one',
          relatedEntityId: item.relatedEntity
            ? (resolveSchema(item.relatedEntity, workflow.value)?.id || null)
            : null
        }

        const nested = normalizeNestedFields(item.fields)
        if (nested) {
          schemaField.fields = nested
        }

        return schemaField
      })
    }

    await workflowApi.addSchemaAndLinkCard({
      schema: newSchema,
      cardId: domainEventCard.id,
      eventId,
      workflow: workflow.value,
      svgGraph: svgGraph.value
    })

    return { success: true }
  }

  const updateDomainEventSchemaFn = async (args) => {
    const { domainEventSchema: schemaRef, name: newName, entity: entityRef, addFields, updateFields, removeFields } = args

    const cardTypesMap = Object.fromEntries(cardTypes.value.map((ct) => [ct.id, ct]))
    const resolved = resolveSchema(schemaRef, workflow.value, cardTypesMap)
    if (!resolved) {
      return { success: false, error: 'Domain event schema not found' }
    }

    const schemas = workflow.value.schemas || []
    const schemaIndex = schemas.findIndex((schema) => schema.id === resolved.id)

    if (schemaIndex === -1) {
      return { success: false, error: 'Domain event schema not found' }
    }

    const schema = schemas[schemaIndex]
    const currentFields = [...(schema.fields || [])]
    const changes = []

    if (removeFields && removeFields.length > 0) {
      for (const fieldName of removeFields) {
        const fieldIndex = currentFields.findIndex((field) => field.name === fieldName)
        if (fieldIndex === -1) {
          return { success: false, error: `Field "${fieldName}" not found` }
        }
        currentFields.splice(fieldIndex, 1)
        changes.push(`removed "${fieldName}"`)
      }
    }

    if (updateFields && updateFields.length > 0) {
      for (const { fieldName, updates } of updateFields) {
        const fieldIndex = currentFields.findIndex((field) => field.name === fieldName)
        if (fieldIndex === -1) {
          return { success: false, error: `Field "${fieldName}" not found` }
        }

        if (updates.name && updates.name !== fieldName) {
          const existingField = currentFields.find((field) => field.name === updates.name)
          if (existingField) {
            return { success: false, error: `Field "${updates.name}" already exists` }
          }
        }

        const existingField = currentFields[fieldIndex]
        const updatedField = { ...existingField }

        if (updates.name !== undefined) {
          updatedField.name = updates.name
        }
        if (updates.cardinality !== undefined) {
          updatedField.cardinality = updates.cardinality
        }
        if (updates.relatedEntity !== undefined) {
          updatedField.relatedEntityId = updates.relatedEntity
            ? (resolveSchema(updates.relatedEntity, workflow.value)?.id || null)
            : null
          if (!updatedField.relatedEntityId) {
            delete updatedField.fields
          }
        }
        if (updates.fields !== undefined) {
          const nested = normalizeNestedFields(updates.fields)
          if (nested) {
            updatedField.fields = nested
          } else {
            delete updatedField.fields
          }
        }

        currentFields[fieldIndex] = updatedField
        changes.push(`updated "${fieldName}"`)
      }
    }

    if (addFields && addFields.length > 0) {
      for (const field of addFields) {
        const existingField = currentFields.find((existing) => existing.name === field.name)
        if (existingField) {
          return { success: false, error: `Field "${field.name}" already exists` }
        }
        const newField = {
          name: field.name,
          cardinality: field.cardinality || 'one-to-one',
          relatedEntityId: field.relatedEntity
            ? (resolveSchema(field.relatedEntity, workflow.value)?.id || null)
            : null
        }

        const nested = normalizeNestedFields(field.fields)
        if (nested) {
          newField.fields = nested
        }

        currentFields.push(newField)
        changes.push(`added "${field.name}"`)
      }
    }

    const updatedSchemas = safeStructuredClone(schemas)
    updatedSchemas[schemaIndex].fields = currentFields

    if (newName) {
      updatedSchemas[schemaIndex].name = newName
    }

    if (entityRef !== undefined) {
      updatedSchemas[schemaIndex].entityId = entityRef
        ? (resolveSchema(entityRef, workflow.value)?.id || null)
        : null
    }

    if (changes.length === 0 && !newName && entityRef === undefined) {
      return { success: false, error: 'No changes specified' }
    }

    await workflowApi.updateSchemas({
      schemas: updatedSchemas,
      workflow: workflow.value
    })

    const allChanges = []
    if (newName) {
      allChanges.push(`renamed to "${newName}"`)
    }
    if (entityRef !== undefined) {
      allChanges.push('entity updated')
    }
    allChanges.push(...changes)

    return { success: true, message: `Domain event schema updated: ${allChanges.join(', ')}` }
  }

  const createReadModelSchema = async (args) => {
    const { domainEvent: eventRef, name, description, fields, entity: entityRef, cardinality } = args

    const resolvedEvent = resolveDomainEvent(eventRef, workflow.value)
    if (!resolvedEvent) {
      return { success: false, error: 'Event not found' }
    }
    const eventId = resolvedEvent.id

    const readModelCardType = cardTypes.value.find((ct) => ct.domainModelRole === DomainModelRole.READ_MODEL)
    if (!readModelCardType) {
      return { success: false, error: 'ReadModel card type not found' }
    }

    const result = await workflowApi.createCard(workflow.value.id, eventId, {
      cardTypeId: readModelCardType.id,
      description: name || ''
    })

    const cardTypesMap = Object.fromEntries(cardTypes.value.map((ct) => [ct.id, ct]))
    const queryCard = (result?.cards || []).slice(-1).find((card) =>
      cardTypesMap[card.cardTypeId]?.domainModelRole === DomainModelRole.READ_MODEL && !card.schemaId
    ) || (result?.cards || []).slice(-1)[0]

    if (!queryCard) {
      return { success: false, error: 'Failed to create ReadModel card' }
    }

    const resolvedEntityId = entityRef
      ? (resolveSchema(entityRef, workflow.value)?.id || null)
      : null

    const newSchema = {
      name: name || SchemaType.QUERY,
      type: SchemaType.QUERY,
      cardinality: cardinality || 'one-to-many',
      fields: (fields || []).map((item) => {
        const schemaField = {
          name: item.name,
          type: item.isFilter ? 'filter' : undefined,
          relatedEntityId: item.relatedEntity
            ? (resolveSchema(item.relatedEntity, workflow.value)?.id || null)
            : null
        }

        if (item.description) {
          schemaField.description = item.description
        }
        if (item.computed) {
          schemaField.computed = true
        }

        const nested = normalizeNestedFields(item.fields)
        if (nested) {
          schemaField.fields = nested
        }

        return schemaField
      }),
      entityId: resolvedEntityId
    }
    if (description) {
      newSchema.description = description
    }

    await workflowApi.addSchemaAndLinkCard({
      schema: newSchema,
      cardId: queryCard.id,
      eventId,
      workflow: workflow.value,
      svgGraph: svgGraph.value
    })

    return { success: true }
  }

  const updateReadModelFields = async (args) => {
    const { readModel: readModelRef, name: newName, description: newDescription, entity: entityRef, addFields, updateFields, removeFields } = args

    const cardTypesMap = Object.fromEntries(cardTypes.value.map((ct) => [ct.id, ct]))
    const resolved = resolveSchema(readModelRef, workflow.value, cardTypesMap)
    if (!resolved) {
      return { success: false, error: 'Read model not found' }
    }

    const schemas = workflow.value.schemas || []
    const schemaIndex = schemas.findIndex((s) => s.id === resolved.id)

    if (schemaIndex === -1) {
      return { success: false, error: 'ReadModel schema not found' }
    }

    const schema = schemas[schemaIndex]
    const currentFields = [...(schema.fields || [])]
    const changes = []

    if (removeFields && removeFields.length > 0) {
      for (const fieldName of removeFields) {
        const fieldIndex = currentFields.findIndex((f) => f.name === fieldName)
        if (fieldIndex === -1) {
          return { success: false, error: `Field "${fieldName}" not found` }
        }
        currentFields.splice(fieldIndex, 1)
        changes.push(`removed "${fieldName}"`)
      }
    }

    if (updateFields && updateFields.length > 0) {
      for (const { fieldName, updates } of updateFields) {
        const fieldIndex = currentFields.findIndex((f) => f.name === fieldName)
        if (fieldIndex === -1) {
          return { success: false, error: `Field "${fieldName}" not found` }
        }

        if (updates.name && updates.name !== fieldName) {
          const existingField = currentFields.find((f) => f.name === updates.name)
          if (existingField) {
            return { success: false, error: `Field "${updates.name}" already exists` }
          }
        }

        const existingField = currentFields[fieldIndex]
        const updatedField = { ...existingField }

        if (updates.name !== undefined) {
          updatedField.name = updates.name
        }
        if (updates.description !== undefined) {
          if (updates.description) {
            updatedField.description = updates.description
          } else {
            delete updatedField.description
          }
        }
        if (updates.isFilter !== undefined) {
          updatedField.type = updates.isFilter ? 'filter' : undefined
        }
        if (updates.computed !== undefined) {
          if (updates.computed) {
            updatedField.computed = true
          } else {
            delete updatedField.computed
          }
        }
        if (updates.relatedEntity !== undefined) {
          updatedField.relatedEntityId = updates.relatedEntity
            ? (resolveSchema(updates.relatedEntity, workflow.value)?.id || null)
            : null
          if (!updatedField.relatedEntityId) {
            delete updatedField.fields
          }
        }
        if (updates.fields !== undefined) {
          const nested = normalizeNestedFields(updates.fields)
          if (nested) {
            updatedField.fields = nested
          } else {
            delete updatedField.fields
          }
        }

        currentFields[fieldIndex] = updatedField
        changes.push(`updated "${fieldName}"`)
      }
    }

    if (addFields && addFields.length > 0) {
      for (const field of addFields) {
        const existingField = currentFields.find((f) => f.name === field.name)
        if (existingField) {
          return { success: false, error: `Field "${field.name}" already exists` }
        }
        const newField = {
          name: field.name,
          type: field.isFilter ? 'filter' : undefined,
          relatedEntityId: field.relatedEntity
            ? (resolveSchema(field.relatedEntity, workflow.value)?.id || null)
            : null
        }

        if (field.description) {
          newField.description = field.description
        }
        if (field.computed) {
          newField.computed = true
        }

        const nested = normalizeNestedFields(field.fields)
        if (nested) {
          newField.fields = nested
        }

        currentFields.push(newField)
        changes.push(`added "${field.name}"`)
      }
    }

    const updatedSchemas = safeStructuredClone(schemas)
    updatedSchemas[schemaIndex].fields = currentFields

    if (newName) {
      updatedSchemas[schemaIndex].name = newName
    }

    if (newDescription !== undefined) {
      if (newDescription) {
        updatedSchemas[schemaIndex].description = newDescription
        changes.push('updated description')
      } else {
        delete updatedSchemas[schemaIndex].description
        changes.push('removed description')
      }
    }

    if (entityRef !== undefined) {
      const resolvedEntityId = entityRef
        ? (resolveSchema(entityRef, workflow.value)?.id || null)
        : null
      updatedSchemas[schemaIndex].entityId = resolvedEntityId
      changes.push(resolvedEntityId ? 'updated queried entity' : 'removed queried entity')
    }

    if (changes.length === 0) {
      return { success: false, error: 'No changes specified' }
    }

    await workflowApi.updateSchemas({
      schemas: updatedSchemas,
      workflow: workflow.value
    })

    return { success: true, message: `ReadModel updated: ${changes.join(', ')}` }
  }

  const regenerateCards = async () => {
    if (!workflow.value.aiVersion) {
      return { success: false, error: 'AI version not configured' }
    }

    const enabledCardTypes = cardTypes.value.filter((ct) => ct.generateWithAI)
    if (!enabledCardTypes.length) {
      return { success: false, error: 'No card types have AI generation enabled' }
    }

    const enabledCardTypeIds = new Set(enabledCardTypes.map((ct) => ct.id))
    let totalDeleted = 0

    for (const event of workflow.value.domainEvents || []) {
      const eventCards = svgGraph.value[event.id]?.cards || []
      const cardsToDelete = eventCards.filter((card) => enabledCardTypeIds.has(card.cardTypeId))

      for (const card of cardsToDelete) {
        await workflowApi.deleteCard(workflow.value.id, { cardId: card.id })
        totalDeleted++
      }
    }

    const response = await generateWorkflowCards({
      workflow: workflow.value,
      cardTypes: cardTypes.value,
      stream: false
    })

    const events = response?.events || []

    if (!events.length) {
      return { success: true, deleted: totalDeleted, created: 0 }
    }

    let totalCreated = 0

    for (const cardData of events) {
      const event = findEventByResponse(cardData)
      if (!event) {
        continue
      }

      for (const cardType of enabledCardTypes) {
        const cardTypeKey = formatCardKey(cardType.title)
        let cardValues = cardData[cardTypeKey]

        if (!cardValues) {
          continue
        }

        if (!Array.isArray(cardValues)) {
          cardValues = [cardValues]
        }

        for (const cardValue of cardValues) {
          const cardText = typeof cardValue === 'object' ? cardValue.name : cardValue
          if (!cardText) {
            continue
          }

          await workflowApi.createCard(workflow.value.id, event.id, {
            cardTypeId: cardType.id,
            description: cardText
          })
          totalCreated++
        }
      }
    }

    return { success: true, deleted: totalDeleted, created: totalCreated }
  }

  const addMissingCards = async () => {
    if (!workflow.value.aiVersion) {
      return { success: false, error: 'AI version not configured' }
    }

    const enabledCardTypes = cardTypes.value.filter((ct) => ct.generateWithAI)
    if (!enabledCardTypes.length) {
      return { success: false, error: 'No card types have AI generation enabled' }
    }

    const response = await generateWorkflowCards({
      workflow: workflow.value,
      cardTypes: cardTypes.value,
      stream: false
    })

    const events = response?.events || []

    if (!events.length) {
      return { success: false, error: 'Failed to generate cards' }
    }

    let totalCreated = 0

    for (const cardData of events) {
      const event = findEventByResponse(cardData)
      if (!event) {
        continue
      }

      const eventCards = svgGraph.value[event.id]?.cards || []
      const existingCardTypeIds = new Set(eventCards.map((card) => card.cardTypeId))

      for (const cardType of enabledCardTypes) {
        if (existingCardTypeIds.has(cardType.id)) {
          continue
        }

        const cardTypeKey = formatCardKey(cardType.title)
        let cardValues = cardData[cardTypeKey]

        if (!cardValues) {
          continue
        }

        if (!Array.isArray(cardValues)) {
          cardValues = [cardValues]
        }

        for (const cardValue of cardValues) {
          const cardText = typeof cardValue === 'object' ? cardValue.name : cardValue
          if (!cardText) {
            continue
          }

          await workflowApi.createCard(workflow.value.id, event.id, {
            cardTypeId: cardType.id,
            description: cardText
          })
          totalCreated++
        }
      }
    }

    return { success: true, created: totalCreated }
  }

  const regenerateCommandSchemas = async () => {
    if (!workflow.value.aiVersion) {
      return { success: false, error: 'AI version not configured' }
    }

    const commandCardType = cardTypes.value.find((ct) => ct.domainModelRole === DomainModelRole.COMMAND)
    if (!commandCardType) {
      return { success: false, error: 'No command card type found' }
    }

    const commandSchemaIds = new Set()
    const existingSchemas = workflow.value.schemas || []
    for (const schema of existingSchemas) {
      if (schema.type === SchemaType.COMMAND) {
        commandSchemaIds.add(schema.id)
      }
    }

    const schemasWithoutCommands = existingSchemas.filter((s) => s.type !== SchemaType.COMMAND)
    const totalDeleted = commandSchemaIds.size

    if (totalDeleted > 0) {
      const currentSvgGraph = safeStructuredClone(svgGraph.value)

      for (const eventId of Object.keys(currentSvgGraph)) {
        const cards = currentSvgGraph[eventId]?.cards || []
        for (const card of cards) {
          if (card.schemaId && commandSchemaIds.has(card.schemaId)) {
            delete card.schemaId
          }
        }
      }

      await workflowApi.updateWorkflow({
        svgGraph: currentSvgGraph,
        svgLanes: svgLanes.value,
        svgGroups: svgGroups.value,
        schemas: schemasWithoutCommands,
        workflow: workflow.value,
        updateNote: {
          action: 'deleted command schemas',
          target: `${totalDeleted} schemas`
        }
      })
    }

    const response = await generateWorkflowCommands({
      workflow: { ...workflow.value, schemas: schemasWithoutCommands },
      cardTypes: cardTypes.value,
      stream: false
    })

    const events = response?.events || []

    if (!events.length) {
      return { success: true, deleted: totalDeleted, created: 0 }
    }

    let totalCreated = 0

    for (const eventData of events) {
      const event = findEventByResponse(eventData)
      if (!event) {
        continue
      }

      const commandCard = cardHelpers.methods.getCommandCard(event)

      if (!commandCard || !eventData.fields?.length) {
        continue
      }

      const fields = parseCommandFields(eventData)

      const newSchema = {
        name: SchemaType.COMMAND,
        type: SchemaType.COMMAND,
        fields
      }

      await workflowApi.addSchemaAndLinkCard({
        schema: newSchema,
        cardId: commandCard.id,
        eventId: event.id,
        workflow: workflow.value,
        svgGraph: svgGraph.value
      })

      totalCreated++
    }

    return { success: true, deleted: totalDeleted, created: totalCreated }
  }

  const addMissingCommandSchemas = async () => {
    if (!workflow.value.aiVersion) {
      return { success: false, error: 'AI version not configured' }
    }

    const commandCardType = cardTypes.value.find((ct) => ct.domainModelRole === DomainModelRole.COMMAND)
    if (!commandCardType) {
      return { success: false, error: 'No command card type found' }
    }

    const response = await generateWorkflowCommands({
      workflow: workflow.value,
      cardTypes: cardTypes.value,
      stream: false
    })

    const events = response?.events || []

    if (!events.length) {
      return { success: false, error: 'Failed to generate commands' }
    }

    let totalCreated = 0

    for (const eventData of events) {
      const event = findEventByResponse(eventData)
      if (!event) {
        continue
      }

      const commandCard = cardHelpers.methods.getCommandCard(event)
      if (!commandCard || commandCard.schemaId || !eventData.fields?.length) {
        continue
      }

      const fields = parseCommandFields(eventData)

      const newSchema = {
        name: SchemaType.COMMAND,
        type: SchemaType.COMMAND,
        fields
      }

      await workflowApi.addSchemaAndLinkCard({
        schema: newSchema,
        cardId: commandCard.id,
        eventId: event.id,
        workflow: workflow.value,
        svgGraph: svgGraph.value
      })

      totalCreated++
    }

    return { success: true, created: totalCreated }
  }

  const regenerateReadModelSchemas = async () => {
    if (!workflow.value.aiVersion) {
      return { success: false, error: 'AI version not configured' }
    }

    const readModelCardType = cardTypes.value.find((ct) => ct.domainModelRole === DomainModelRole.READ_MODEL)
    if (!readModelCardType) {
      return { success: false, error: 'No read model card type found' }
    }

    const querySchemaIds = new Set()
    const existingSchemas = workflow.value.schemas || []
    for (const schema of existingSchemas) {
      if (schema.type === SchemaType.QUERY) {
        querySchemaIds.add(schema.id)
      }
    }

    const schemasWithoutQueries = existingSchemas.filter((s) => s.type !== SchemaType.QUERY)
    const totalDeleted = querySchemaIds.size

    if (totalDeleted > 0) {
      const currentSvgGraph = safeStructuredClone(svgGraph.value)

      for (const eventId of Object.keys(currentSvgGraph)) {
        const cards = currentSvgGraph[eventId]?.cards || []
        for (const card of cards) {
          if (card.schemaId && querySchemaIds.has(card.schemaId)) {
            delete card.schemaId
          }
        }
      }

      await workflowApi.updateWorkflow({
        svgGraph: currentSvgGraph,
        svgLanes: svgLanes.value,
        svgGroups: svgGroups.value,
        schemas: schemasWithoutQueries,
        workflow: workflow.value,
        updateNote: {
          action: 'deleted read model schemas',
          target: `${totalDeleted} schemas`
        }
      })
    }

    const response = await generateWorkflowReadModels({
      workflow: { ...workflow.value, schemas: schemasWithoutQueries },
      cardTypes: cardTypes.value,
      stream: false
    })

    const events = response?.events || []

    if (!events.length) {
      return { success: true, deleted: totalDeleted, created: 0 }
    }

    let totalCreated = 0

    for (const eventData of events) {
      const event = findEventByResponse(eventData)
      if (!event || !eventData.readModelFields?.length) {
        continue
      }

      const eventCards = svgGraph.value[event.id]?.cards || []
      const queryCard = eventCards.find((c) => c.cardTypeId === readModelCardType.id)

      const fields = parseReadModelFields(eventData.readModelFields)

      const entities = workflow.value.schemas?.filter((s) => s.type === SchemaType.ENTITY) || []
      const queriedEntity = eventData.queriedEntity
        ? entities.find((e) => e.name.toLowerCase() === eventData.queriedEntity.toLowerCase())
        : null

      const newSchema = {
        name: SchemaType.QUERY,
        type: SchemaType.QUERY,
        cardinality: eventData.cardinality || 'one-to-many',
        fields,
        entityId: queriedEntity?.id || null
      }
      if (eventData.readModelDescription) {
        newSchema.description = eventData.readModelDescription
      }

      await workflowApi.addSchemaAndLinkCard({
        schema: newSchema,
        cardId: queryCard?.id || null,
        eventId: event.id,
        workflow: workflow.value,
        svgGraph: svgGraph.value
      })

      totalCreated++
    }

    return { success: true, deleted: totalDeleted, created: totalCreated }
  }

  const addMissingReadModelSchemas = async () => {
    if (!workflow.value.aiVersion) {
      return { success: false, error: 'AI version not configured' }
    }

    const readModelCardType = cardTypes.value.find((ct) => ct.domainModelRole === DomainModelRole.READ_MODEL)
    if (!readModelCardType) {
      return { success: false, error: 'No read model card type found' }
    }

    const response = await generateWorkflowReadModels({
      workflow: workflow.value,
      cardTypes: cardTypes.value,
      stream: false
    })

    const events = response?.events || []

    if (!events.length) {
      return { success: false, error: 'Failed to generate read models' }
    }

    let totalCreated = 0

    for (const eventData of events) {
      const event = findEventByResponse(eventData)
      if (!event || !eventData.readModelFields?.length) {
        continue
      }

      const queryCards = cardHelpers.methods.getQueryCards(event)
      const cardsWithoutSchema = queryCards.filter((card) => !card.schemaId)

      if (!cardsWithoutSchema.length) {
        continue
      }

      const fields = parseReadModelFields(eventData.readModelFields)

      const entities = workflow.value.schemas?.filter((s) => s.type === SchemaType.ENTITY) || []
      const queriedEntity = eventData.queriedEntity
        ? entities.find((e) => e.name.toLowerCase() === eventData.queriedEntity.toLowerCase())
        : null

      for (const queryCard of cardsWithoutSchema) {
        const newSchema = {
          name: SchemaType.QUERY,
          type: SchemaType.QUERY,
          cardinality: eventData.cardinality || 'one-to-many',
          fields,
          entityId: queriedEntity?.id || null
        }

        await workflowApi.addSchemaAndLinkCard({
          schema: newSchema,
          cardId: queryCard.id,
          eventId: event.id,
          workflow: workflow.value,
          svgGraph: svgGraph.value
        })

        totalCreated++
      }
    }

    return { success: true, created: totalCreated }
  }

  const regenerateEntities = async () => {
    if (!workflow.value.aiVersion) {
      return { success: false, error: 'AI version not configured' }
    }

    const response = await generateEntitiesFromWorkflow({
      workflow: workflow.value,
      cardTypes: cardTypes.value
    })

    const entities = response?.entities || []

    if (!entities.length) {
      return { success: false, error: 'Failed to generate entities' }
    }

    let newSchemas = safeStructuredClone(workflow.value.schemas || [])

    const nonEntitySchemas = newSchemas.filter((s) => s.type !== SchemaType.ENTITY)
    const totalDeleted = newSchemas.length - nonEntitySchemas.length
    newSchemas = nonEntitySchemas

    const entityNameToIdMap = new Map()

    const newEntityUUIDs = new Map()
    for (const entity of entities) {
      const uuid = crypto.randomUUID()
      newEntityUUIDs.set(entity.name, uuid)
      entityNameToIdMap.set(entity.name.toLowerCase(), uuid)
    }

    for (const entityFromLLM of entities) {
      const generatedEntity = parseEntityResponse(entityFromLLM, entityNameToIdMap)
      generatedEntity.id = newEntityUUIDs.get(entityFromLLM.name)
      newSchemas = [...newSchemas, generatedEntity]
    }

    await workflowApi.updateSchemas({
      schemas: newSchemas,
      workflow: workflow.value
    })

    return { success: true, created: entities.length, deleted: totalDeleted }
  }

  const addMissingEntities = async () => {
    if (!workflow.value.aiVersion) {
      return { success: false, error: 'AI version not configured' }
    }

    const response = await generateEntitiesFromWorkflow({
      workflow: workflow.value,
      cardTypes: cardTypes.value
    })

    const entities = response?.entities || []

    if (!entities.length) {
      return { success: false, error: 'Failed to generate entities' }
    }

    let newSchemas = safeStructuredClone(workflow.value.schemas || [])

    const existingEntityNames = new Set(
      newSchemas.filter((s) => s.type === SchemaType.ENTITY).map((s) => s.name.toLowerCase())
    )

    const newEntities = entities.filter((entity) => !existingEntityNames.has(entity.name.toLowerCase()))

    if (newEntities.length === 0) {
      return { success: true, created: 0, skipped: entities.length }
    }

    const entityNameToIdMap = new Map()

    for (const schema of newSchemas) {
      if (schema.type === SchemaType.ENTITY) {
        entityNameToIdMap.set(schema.name.toLowerCase(), schema.id)
      }
    }

    const newEntityUUIDs = new Map()
    for (const entity of newEntities) {
      const uuid = crypto.randomUUID()
      newEntityUUIDs.set(entity.name, uuid)
      entityNameToIdMap.set(entity.name.toLowerCase(), uuid)
    }

    for (const entityFromLLM of newEntities) {
      const generatedEntity = parseEntityResponse(entityFromLLM, entityNameToIdMap)
      generatedEntity.id = newEntityUUIDs.get(entityFromLLM.name)
      newSchemas = [...newSchemas, generatedEntity]
    }

    await workflowApi.updateSchemas({
      schemas: newSchemas,
      workflow: workflow.value
    })

    return { success: true, created: newEntities.length, skipped: entities.length - newEntities.length }
  }

  const createEntitiesFromDescription = async (args = {}) => {
    const { description, replaceExisting = false } = args

    if (!description) {
      return { success: false, error: 'Description is required' }
    }

    if (!workflow.value.aiVersion) {
      return { success: false, error: 'AI version not configured' }
    }

    const response = await generateEntitiesFromDescription({
      workflow: workflow.value,
      cardTypes: cardTypes.value,
      userDescription: description
    })

    const entities = response?.entities || []

    if (!entities.length) {
      return { success: false, error: 'Failed to generate entities from description' }
    }

    let newSchemas = safeStructuredClone(workflow.value.schemas || [])
    let totalDeleted = 0

    if (replaceExisting) {
      const nonEntitySchemas = newSchemas.filter((s) => s.type !== SchemaType.ENTITY)
      totalDeleted = newSchemas.length - nonEntitySchemas.length
      newSchemas = nonEntitySchemas
    }

    const existingEntityNames = new Set(
      newSchemas.filter((s) => s.type === SchemaType.ENTITY).map((s) => s.name.toLowerCase())
    )

    const newEntities = entities.filter((entity) => !existingEntityNames.has(entity.name.toLowerCase()))

    if (newEntities.length === 0 && !replaceExisting) {
      return { success: true, created: 0, skipped: entities.length }
    }

    const entityNameToIdMap = new Map()

    for (const schema of newSchemas) {
      if (schema.type === SchemaType.ENTITY) {
        entityNameToIdMap.set(schema.name.toLowerCase(), schema.id)
      }
    }

    const newEntityUUIDs = new Map()
    for (const entity of newEntities) {
      const uuid = crypto.randomUUID()
      newEntityUUIDs.set(entity.name, uuid)
      entityNameToIdMap.set(entity.name.toLowerCase(), uuid)
    }

    for (const entityFromLLM of newEntities) {
      const generatedEntity = parseEntityResponse(entityFromLLM, entityNameToIdMap)
      generatedEntity.id = newEntityUUIDs.get(entityFromLLM.name)
      newSchemas = [...newSchemas, generatedEntity]
    }

    await workflowApi.updateSchemas({
      schemas: newSchemas,
      workflow: workflow.value
    })

    if (replaceExisting) {
      return { success: true, created: newEntities.length, deleted: totalDeleted }
    }

    return { success: true, created: newEntities.length, skipped: entities.length - newEntities.length }
  }

  const createCommandFieldsFromDescription = async (args = {}) => {
    const { description, replaceExisting = false } = args

    if (!description) {
      return { success: false, error: 'Description is required' }
    }

    if (!workflow.value.aiVersion) {
      return { success: false, error: 'AI version not configured' }
    }

    const commandCardType = cardTypes.value.find((ct) => ct.domainModelRole === DomainModelRole.COMMAND)
    if (!commandCardType) {
      return { success: false, error: 'No command card type found' }
    }

    let schemasForGeneration = workflow.value.schemas || []
    let totalDeleted = 0

    if (replaceExisting) {
      const commandSchemaIds = new Set()
      for (const schema of schemasForGeneration) {
        if (schema.type === SchemaType.COMMAND) {
          commandSchemaIds.add(schema.id)
        }
      }

      const schemasWithoutCommands = schemasForGeneration.filter((s) => s.type !== SchemaType.COMMAND)
      totalDeleted = commandSchemaIds.size

      if (totalDeleted > 0) {
        const currentSvgGraph = safeStructuredClone(svgGraph.value)

        for (const eventId of Object.keys(currentSvgGraph)) {
          const cards = currentSvgGraph[eventId]?.cards || []
          for (const card of cards) {
            if (card.schemaId && commandSchemaIds.has(card.schemaId)) {
              delete card.schemaId
            }
          }
        }

        await workflowApi.updateWorkflow({
          svgGraph: currentSvgGraph,
          svgLanes: svgLanes.value,
          svgGroups: svgGroups.value,
          schemas: schemasWithoutCommands,
          workflow: workflow.value,
          updateNote: {
            action: 'deleted command schemas',
            target: `${totalDeleted} schemas`
          }
        })

        schemasForGeneration = schemasWithoutCommands
      }
    }

    const response = await generateCommandsFromDescription({
      workflow: { ...workflow.value, schemas: schemasForGeneration },
      cardTypes: cardTypes.value,
      userDescription: description,
      stream: false
    })

    const events = response?.events || []

    if (!events.length) {
      return { success: false, error: 'Failed to generate command fields from description' }
    }

    let totalCreated = 0

    for (const eventData of events) {
      const event = findEventByResponse(eventData)
      if (!event) {
        continue
      }

      const commandCard = cardHelpers.methods.getCommandCard(event)
      if (!commandCard || (!replaceExisting && commandCard.schemaId) || !eventData.fields?.length) {
        continue
      }

      const fields = parseCommandFields(eventData)

      const newSchema = {
        name: SchemaType.COMMAND,
        type: SchemaType.COMMAND,
        fields
      }

      await workflowApi.addSchemaAndLinkCard({
        schema: newSchema,
        cardId: commandCard.id,
        eventId: event.id,
        workflow: workflow.value,
        svgGraph: svgGraph.value
      })

      totalCreated++
    }

    if (replaceExisting) {
      return { success: true, created: totalCreated, deleted: totalDeleted }
    }

    return { success: true, created: totalCreated }
  }

  const createReadModelFieldsFromDescription = async (args = {}) => {
    const { description, replaceExisting = false } = args

    if (!description) {
      return { success: false, error: 'Description is required' }
    }

    if (!workflow.value.aiVersion) {
      return { success: false, error: 'AI version not configured' }
    }

    const readModelCardType = cardTypes.value.find((ct) => ct.domainModelRole === DomainModelRole.READ_MODEL)
    if (!readModelCardType) {
      return { success: false, error: 'No read model card type found' }
    }

    let schemasForGeneration = workflow.value.schemas || []
    let totalDeleted = 0

    if (replaceExisting) {
      const querySchemaIds = new Set()
      for (const schema of schemasForGeneration) {
        if (schema.type === SchemaType.QUERY) {
          querySchemaIds.add(schema.id)
        }
      }

      const schemasWithoutQueries = schemasForGeneration.filter((s) => s.type !== SchemaType.QUERY)
      totalDeleted = querySchemaIds.size

      if (totalDeleted > 0) {
        const currentSvgGraph = safeStructuredClone(svgGraph.value)

        for (const eventId of Object.keys(currentSvgGraph)) {
          const cards = currentSvgGraph[eventId]?.cards || []
          for (const card of cards) {
            if (card.schemaId && querySchemaIds.has(card.schemaId)) {
              delete card.schemaId
            }
          }
        }

        await workflowApi.updateWorkflow({
          svgGraph: currentSvgGraph,
          svgLanes: svgLanes.value,
          svgGroups: svgGroups.value,
          schemas: schemasWithoutQueries,
          workflow: workflow.value,
          updateNote: {
            action: 'deleted read model schemas',
            target: `${totalDeleted} schemas`
          }
        })

        schemasForGeneration = schemasWithoutQueries
      }
    }

    const response = await generateReadModelsFromDescription({
      workflow: { ...workflow.value, schemas: schemasForGeneration },
      cardTypes: cardTypes.value,
      userDescription: description,
      stream: false
    })

    const events = response?.events || []

    if (!events.length) {
      return { success: false, error: 'Failed to generate read model fields from description' }
    }

    let totalCreated = 0

    for (const eventData of events) {
      const event = findEventByResponse(eventData)
      if (!event || !eventData.readModelFields?.length) {
        continue
      }

      const queryCards = cardHelpers.methods.getQueryCards(event)
      const cardsToProcess = replaceExisting
        ? queryCards
        : queryCards.filter((card) => !card.schemaId)

      if (!cardsToProcess.length) {
        continue
      }

      const fields = parseReadModelFields(eventData.readModelFields)

      const entities = workflow.value.schemas?.filter((s) => s.type === SchemaType.ENTITY) || []
      const queriedEntity = eventData.queriedEntity
        ? entities.find((e) => e.name.toLowerCase() === eventData.queriedEntity.toLowerCase())
        : null

      for (const queryCard of cardsToProcess) {
        const newSchema = {
          name: SchemaType.QUERY,
          type: SchemaType.QUERY,
          cardinality: eventData.cardinality || 'one-to-many',
          fields,
          entityId: queriedEntity?.id || null
        }

        await workflowApi.addSchemaAndLinkCard({
          schema: newSchema,
          cardId: queryCard.id,
          eventId: event.id,
          workflow: workflow.value,
          svgGraph: svgGraph.value
        })

        totalCreated++
      }
    }

    if (replaceExisting) {
      return { success: true, created: totalCreated, deleted: totalDeleted }
    }

    return { success: true, created: totalCreated }
  }

  const createCardsFromDescription = async (args = {}) => {
    const { description, replaceExisting = false } = args

    if (!description) {
      return { success: false, error: 'Description is required' }
    }

    if (!workflow.value.aiVersion) {
      return { success: false, error: 'AI version not configured' }
    }

    const enabledCardTypes = cardTypes.value.filter((ct) => ct.generateWithAI)
    if (!enabledCardTypes.length) {
      return { success: false, error: 'No card types have AI generation enabled' }
    }

    const enabledCardTypeIds = new Set(enabledCardTypes.map((ct) => ct.id))
    let totalDeleted = 0

    if (replaceExisting) {
      for (const event of workflow.value.domainEvents || []) {
        const eventCards = svgGraph.value[event.id]?.cards || []
        const cardsToDelete = eventCards.filter((card) => enabledCardTypeIds.has(card.cardTypeId))

        for (const card of cardsToDelete) {
          await workflowApi.deleteCard(workflow.value.id, { cardId: card.id })
          totalDeleted++
        }
      }
    }

    const response = await generateCardsFromDescription({
      workflow: workflow.value,
      cardTypes: cardTypes.value,
      userDescription: description,
      stream: false
    })

    const events = response?.events || []

    if (!events.length) {
      return { success: false, error: 'Failed to generate cards from description' }
    }

    let totalCreated = 0

    for (const cardData of events) {
      const event = findEventByResponse(cardData)
      if (!event) {
        continue
      }

      const eventCards = svgGraph.value[event.id]?.cards || []
      const existingCardTypeIds = replaceExisting
        ? new Set()
        : new Set(eventCards.map((card) => card.cardTypeId))

      for (const cardType of enabledCardTypes) {
        if (existingCardTypeIds.has(cardType.id)) {
          continue
        }

        const cardTypeKey = formatCardKey(cardType.title)
        let cardValues = cardData[cardTypeKey]

        if (!cardValues) {
          continue
        }

        if (!Array.isArray(cardValues)) {
          cardValues = [cardValues]
        }

        for (const cardValue of cardValues) {
          const cardText = typeof cardValue === 'object' ? cardValue.name : cardValue
          if (!cardText) {
            continue
          }

          await workflowApi.createCard(workflow.value.id, event.id, {
            cardTypeId: cardType.id,
            description: cardText
          })
          totalCreated++
        }
      }
    }

    if (replaceExisting) {
      return { success: true, created: totalCreated, deleted: totalDeleted }
    }

    return { success: true, created: totalCreated }
  }

  const planWorkflowWithAI = async (args = {}) => {
    const { description } = args

    if (!description) {
      return { success: false, error: 'Description is required' }
    }

    if (!workflow.value.aiVersion) {
      return { success: false, error: 'AI version not configured' }
    }

    const response = await generateWorkflowDiagram({
      workflow: {
        ...workflow.value,
        aiDescription: description
      },
      cardTypes: cardTypes.value,
      stream: false
    })

    const events = response?.events || []

    if (!events.length) {
      return { success: false, error: 'Failed to generate events from description' }
    }

    return {
      success: true,
      events
    }
  }

  const validateDomainModel = (args = {}) => {
    const { scope = 'all' } = args
    const result = validateWorkflowUtil(workflow.value, scope)

    return {
      success: true,
      ...result
    }
  }

  const analyzeDomainModel = (args = {}) => {
    const { businessContext, focusAreas } = args
    const schemas = workflow.value.schemas || []
    const domainEvents = workflow.value.domainEvents || []
    const boundedContexts = workflow.value.boundedContexts || []
    const lanes = workflow.value.lanes || []

    const entities = schemas
      .filter((s) => s.type === SchemaType.ENTITY)
      .map((entity) => {
        const fields = (entity.fields || []).map((field) => {
          const relatedEntity = field.relatedEntityId
            ? schemas.find((s) => s.id === field.relatedEntityId)
            : null

          return {
            name: field.name,
            dataType: field.dataType || 'string',
            isRequired: (field.tags || []).includes('REQUIRED'),
            cardinality: field.cardinality || 'one-to-one',
            relatedEntity: relatedEntity?.name || null
          }
        })

        return {
          id: entity.id,
          name: entity.name,
          fields,
          fieldCount: fields.length,
          relationshipCount: fields.filter((f) => f.relatedEntity).length
        }
      })

    const commands = []
    const readModels = []
    const aggregates = []

    for (const event of domainEvents) {
      const eventCards = event.cards || []

      for (const card of eventCards) {
        const cardType = card.cardType || (cardTypes.value || []).find((ct) => ct.id === card.cardTypeId)
        const schema = card.schemaId ? schemas.find((s) => s.id === card.schemaId) : null

        if (cardType?.domainModelRole === DomainModelRole.COMMAND) {
          commands.push({
            eventId: event.id,
            eventName: event.description,
            cardId: card.id,
            hasSchema: !!schema,
            schemaFields: schema?.fields?.map((f) => ({
              name: f.name,
              cardinality: f.cardinality,
              isRequired: (f.tags || []).includes('REQUIRED'),
              relatedEntity: f.relatedEntityId
                ? schemas.find((s) => s.id === f.relatedEntityId)?.name
                : null
            })) || []
          })
        }

        if (cardType?.domainModelRole === DomainModelRole.READ_MODEL) {
          const queriedEntity = schema?.entityId
            ? schemas.find((s) => s.id === schema.entityId)
            : null

          readModels.push({
            eventId: event.id,
            eventName: event.description,
            cardId: card.id,
            cardName: card.name,
            cardinality: schema?.cardinality || 'one-to-many',
            hasSchema: !!schema,
            queriedEntity: queriedEntity?.name || null,
            schemaFields: schema?.fields?.map((f) => f.name) || []
          })
        }

        if (cardType?.domainModelRole === DomainModelRole.AGGREGATE_ROOT) {
          const linkedEntity = card.schemaId
            ? schemas.find((s) => s.id === card.schemaId)
            : null

          aggregates.push({
            eventId: event.id,
            eventName: event.description,
            cardId: card.id,
            linkedEntity: linkedEntity?.name || null
          })
        }
      }
    }

    const workflowFlow = domainEvents.map((event) => {
      const graphNode = svgGraph.value[event.id]
      const lane = lanes.find((l) => l.id === graphNode?.laneId)

      return {
        id: event.id,
        name: event.description,
        type: graphNode?.type || 'bpmn:Task',
        lane: lane?.name || null,
        hasCommand: commands.some((c) => c.eventId === event.id),
        hasReadModel: readModels.some((r) => r.eventId === event.id),
        hasAggregate: aggregates.some((a) => a.eventId === event.id)
      }
    })

    const entityRelationships = []
    for (const entity of entities) {
      for (const field of entity.fields) {
        if (field.relatedEntity) {
          entityRelationships.push({
            from: entity.name,
            to: field.relatedEntity,
            fieldName: field.name,
            cardinality: field.cardinality
          })
        }
      }
    }

    const summary = {
      totalEvents: domainEvents.length,
      totalEntities: entities.length,
      totalCommands: commands.length,
      totalReadModels: readModels.length,
      totalAggregates: aggregates.length,
      totalBoundedContexts: boundedContexts.length,
      totalLanes: lanes.length,
      eventsWithoutCommands: workflowFlow.filter((e) => e.type === 'bpmn:Task' && !e.hasCommand).map((e) => e.name),
      eventsWithoutReadModels: workflowFlow.filter((e) => e.type === 'bpmn:Task' && !e.hasReadModel).map((e) => e.name),
      commandsWithoutSchema: commands.filter((c) => !c.hasSchema).length,
      readModelsWithoutSchema: readModels.filter((r) => !r.hasSchema).length,
      readModelsWithoutQueriedEntity: readModels.filter((r) => !r.queriedEntity).length
    }

    return {
      success: true,
      workflowName: workflow.value.name,
      businessContext: businessContext || null,
      focusAreas: focusAreas || ['relationships', 'naming', 'aggregates', 'flow', 'completeness', 'ddd-patterns'],
      summary,
      entities,
      entityRelationships,
      commands,
      readModels,
      aggregates,
      workflowFlow,
      boundedContexts: boundedContexts.map((bc) => ({
        id: bc.id,
        name: bc.name,
        color: bc.color
      })),
      lanes: lanes.map((l) => l.name)
    }
  }

  const rollbackWorkflow = async (args) => {
    const { version } = args

    if (!version || version < 1) {
      return { success: false, error: 'Invalid version number' }
    }

    if (version >= workflow.value.version) {
      return {
        success: false,
        error: `Cannot rollback to version ${version}. Current version is ${workflow.value.version}. You can only rollback to a previous version.`
      }
    }

    try {
      await store.dispatch('workflow/restorePreviousWorkflowVersion', {
        restoreVersion: version,
        workflow: workflow.value
      })

      return {
        success: true,
        message: `Rolled back to version ${version}.`
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to rollback workflow'
      }
    }
  }

  const functions = {
    [functionType.UPDATE_WORKFLOW_NAME]: updateWorkflowName,
    [functionType.CREATE_EVENT]: createEvent,
    [functionType.UPDATE_EVENT_DESCRIPTION]: updateEventDescription,
    [functionType.DELETE_EVENT]: deleteEvent,
    [functionType.CREATE_LANE]: createLane,
    [functionType.UPDATE_LANE_NAME]: updateLaneName,
    [functionType.DELETE_LANE]: deleteLane,
    [functionType.MOVE_LANE]: moveLane,
    [functionType.CREATE_CARD]: createCard,
    [functionType.DELETE_CARD]: deleteCard,
    [functionType.UPDATE_CARD]: updateCard,
    [functionType.VOTE_CARD]: voteCard,
    [functionType.CREATE_ENTITY]: createEntity,
    [functionType.UPDATE_ENTITY]: updateEntity,
    [functionType.DELETE_ENTITY]: deleteEntity,
    [functionType.CREATE_COMMAND]: createCommandSchema,
    [functionType.UPDATE_COMMAND]: updateCommandFields,
    [functionType.CREATE_DOMAIN_EVENT_SCHEMA]: createDomainEventSchemaFn,
    [functionType.UPDATE_DOMAIN_EVENT_SCHEMA]: updateDomainEventSchemaFn,
    [functionType.CREATE_READ_MODEL]: createReadModelSchema,
    [functionType.UPDATE_READ_MODEL]: updateReadModelFields,
    [functionType.REGENERATE_CARDS]: regenerateCards,
    [functionType.ADD_MISSING_CARDS]: addMissingCards,
    [functionType.REGENERATE_COMMANDS]: regenerateCommandSchemas,
    [functionType.ADD_MISSING_COMMANDS]: addMissingCommandSchemas,
    [functionType.REGENERATE_READ_MODELS]: regenerateReadModelSchemas,
    [functionType.ADD_MISSING_READ_MODELS]: addMissingReadModelSchemas,
    [functionType.REGENERATE_ENTITIES]: regenerateEntities,
    [functionType.ADD_MISSING_ENTITIES]: addMissingEntities,
    [functionType.GENERATE_ENTITIES_FROM_DESCRIPTION]: createEntitiesFromDescription,
    [functionType.GENERATE_COMMANDS_FROM_DESCRIPTION]: createCommandFieldsFromDescription,
    [functionType.GENERATE_READ_MODELS_FROM_DESCRIPTION]: createReadModelFieldsFromDescription,
    [functionType.GENERATE_CARDS_FROM_DESCRIPTION]: createCardsFromDescription,
    [functionType.PLAN_WORKFLOW_WITH_AI]: planWorkflowWithAI,
    [functionType.VALIDATE_DOMAIN_MODEL]: validateDomainModel,
    [functionType.ANALYZE_DOMAIN_MODEL]: analyzeDomainModel,
    [functionType.ROLLBACK_WORKFLOW]: rollbackWorkflow
  }

  const createAbortError = () => {
    try {
      // Standard DOMException in browsers
      return new DOMException('Aborted', 'AbortError')
    } catch {
      const err = new Error('Aborted')
      err.name = 'AbortError'
      return err
    }
  }

  const shouldStop = (options) => {
    return !!(options?.signal?.aborted || options?.shouldStop?.())
  }

  const withWorkflowApiCancellation = async (options, fn) => {
    if (!options) {
      return await fn()
    }

    const wrap = (methodName, progressText) => {
      const original = workflowApi?.[methodName]
      if (typeof original !== 'function') {
        return null
      }

      workflowApi[methodName] = async (...methodArgs) => {
        if (shouldStop(options)) {
          throw createAbortError()
        }
        if (progressText && typeof options.onProgress === 'function') {
          options.onProgress(progressText)
        }
        // Preserve `this` for static/class-style methods that use `this.*`
        return await original.apply(workflowApi, methodArgs)
      }

      return original
    }

    const originals = {
      deleteCard: wrap('deleteCard', 'Deleting…'),
      createCard: wrap('createCard', 'Creating…'),
      updateWorkflow: wrap('updateWorkflow', 'Updating workflow…'),
      updateSchemas: wrap('updateSchemas', 'Updating schemas…'),
      deleteEventFromWorkflow: wrap('deleteEventFromWorkflow', 'Deleting…'),
      deleteEventsFromWorkflow: wrap('deleteEventsFromWorkflow', 'Deleting…')
    }

    try {
      if (shouldStop(options)) {
        throw createAbortError()
      }
      return await fn()
    } finally {
      for (const [key, original] of Object.entries(originals)) {
        if (original) {
          workflowApi[key] = original
        }
      }
    }
  }

  const callFunction = async (name, args, options) => {
    if (!functions[name]) {
      return
    }

    return await withWorkflowApiCancellation(options, async () => {
      return await functions[name](args)
    })
  }

  return {
    callFunction
  }
}
